/**
 * Promocoes no painel (spec 067): o que a tela escreve e converte, sem regra de negocio. Quando a promocao vale, quanto
 * ela tira e a situacao (valendo, agendada, pausada, encerrada) quem decide e o servico de pagamentos; aqui so o texto
 * do "quando vale" e as conversoes dos campos (hora de Brasilia, dia e mes) pro formato que o servico recebe.
 */
import { formatarMoeda } from "@mtc/ui";

const FUSO = "America/Sao_Paulo";

/** Como a lista e o formulario recebem do servico (`PromocaoNaTela` do pagamentos). */
export type Promocao = {
  id: string;
  nome: string;
  chamada: string | null;
  tema: string;
  tipo: "PERCENTUAL" | "VALOR" | string;
  percentual: number | null;
  valor_centavos: number | null;
  vigencia: "PERIODO" | "SEMANAL" | "ANUAL" | string;
  comeca_em: string | null;
  termina_em: string | null;
  dias_da_semana: number[];
  hora_inicio: string | null;
  hora_fim: string | null;
  anual_inicio: string | null;
  anual_fim: string | null;
  todos_os_planos: boolean;
  planos: string[];
  so_clientes: boolean;
  clientes: string[];
  pausada: boolean;
  situacao: "VALENDO" | "AGENDADA" | "PAUSADA" | "ENCERRADA" | string;
  termina_agora_em: string | null;
  vendas: { pagas: number; desconto_centavos: number };
};

/** 0 = domingo, como o servico. */
export const DIAS_DA_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"] as const;
/** No texto a semana comeca na segunda: "sexta, sábado e domingo", nao "domingo, sexta e sábado". */
const ORDEM_NO_TEXTO = [1, 2, 3, 4, 5, 6, 0];

const lista = (itens: string[]) => (itens.length > 1 ? `${itens.slice(0, -1).join(", ")} e ${itens[itens.length - 1]}` : (itens[0] ?? ""));

/** "27/11/2026 00:00": o instante na hora de Brasilia. */
export function dataHoraEmBrasilia(iso: string): string {
  const p = Object.fromEntries(new Intl.DateTimeFormat("pt-BR", { timeZone: FUSO, day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}

/** "11-24" vira "24/11". */
export const mmddParaDdmm = (mmdd: string | null | undefined) => (mmdd && /^\d{2}-\d{2}$/.test(mmdd) ? `${mmdd.slice(3, 5)}/${mmdd.slice(0, 2)}` : "");

/**
 * O "quando vale" da lista, em texto: "de 27/11/2026 00:00 a 01/12/2026 00:00", "toda sexta, das 18:00 às 23:59",
 * "24/11 a 30/11, todo ano".
 */
export function quandoVale(p: Pick<Promocao, "vigencia" | "comeca_em" | "termina_em" | "dias_da_semana" | "hora_inicio" | "hora_fim" | "anual_inicio" | "anual_fim">): string {
  if (p.vigencia === "PERIODO") {
    const de = p.comeca_em ? dataHoraEmBrasilia(p.comeca_em) : null;
    const ate = p.termina_em ? dataHoraEmBrasilia(p.termina_em) : null;
    if (de && ate) return `de ${de} a ${ate}`;
    if (de) return `a partir de ${de}, sem data pra acabar`;
    if (ate) return `até ${ate}`;
    return "sem data pra acabar";
  }
  if (p.vigencia === "SEMANAL") {
    const dias = ORDEM_NO_TEXTO.filter((d) => p.dias_da_semana.includes(d));
    const nome = (d: number) => DIAS_DA_SEMANA[d]!.toLowerCase();
    // Sabado e domingo sao "todo"; os outros, "toda".
    const quais = dias.length === 7 ? "todos os dias" : dias.length === 1 ? `${dias[0] === 0 || dias[0] === 6 ? "todo" : "toda"} ${nome(dias[0]!)}` : `${lista(dias.map(nome))}, toda semana`;
    return p.hora_inicio && p.hora_fim ? `${quais}, das ${p.hora_inicio} às ${p.hora_fim}` : quais;
  }
  if (p.vigencia === "ANUAL") return `${mmddParaDdmm(p.anual_inicio)} a ${mmddParaDdmm(p.anual_fim)}, todo ano`;
  return "";
}

/** "30%" ou o valor fixo na moeda da vitrine ("R$ 100,00", "US$ 50,00"). */
export const descontoDa = (p: Pick<Promocao, "tipo" | "percentual" | "valor_centavos">, moeda: string) => (p.tipo === "PERCENTUAL" ? `${p.percentual ?? 0}%` : formatarMoeda(p.valor_centavos ?? 0, moeda));

/** O campo datetime-local ("2026-11-27T00:00") e hora de Brasilia: vira o instante ISO. Vazio ou invalido: nulo. */
export function localParaIso(local: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(local)) return null;
  // Brasilia e -03:00 o ano todo desde 2019 (sem horario de verao), como o resto do painel (cupom, mensalidade).
  const d = new Date(`${local}:00-03:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** O instante ISO no formato do campo datetime-local, na hora de Brasilia. */
export function isoParaLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: FUSO, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Enquanto digita: so numeros, com a barra depois do dia ("2411" vira "24/11"). */
export function mascaraDdmm(texto: string): string {
  const n = texto.replace(/\D/g, "").slice(0, 4);
  return n.length > 2 ? `${n.slice(0, 2)}/${n.slice(2)}` : n;
}

/** "24/11" vira "11-24" (o formato do servico). Dia que nao existe no mes: nulo (29/02 vale, o ano bissexto decide). */
export function ddmmParaMmdd(ddmm: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})$/.exec(ddmm.trim());
  if (!m) return null;
  const dia = Number(m[1]);
  const mes = Number(m[2]);
  if (mes < 1 || mes > 12 || dia < 1 || dia > [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mes - 1]!) return null;
  return `${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

/** Um CPF por linha (virgula e ponto e virgula tambem separam, pra quem cola de planilha). */
export const cpfsDoTexto = (texto: string) => texto.split(/[\n,;]+/).map((c) => c.trim()).filter(Boolean);

// ---------------------------------------------------------------- formulario

export type Vigencia = "PERIODO" | "SEMANAL" | "ANUAL";
export type FormularioDaPromocao = {
  nome: string;
  chamada: string;
  tema: string;
  tipo: "PERCENTUAL" | "VALOR";
  percentual: string;
  valor: number;
  vigencia: Vigencia;
  /** datetime-local, hora de Brasilia. */
  comeca: string;
  termina: string;
  dias: number[];
  /** HH:MM; os dois vazios = o dia todo. */
  das: string;
  ate: string;
  /** dd/mm. */
  deDia: string;
  ateDia: string;
  todosOsPlanos: boolean;
  planos: string[];
  soClientes: boolean;
  cpfs: string;
};

export const FORMULARIO_VAZIO: FormularioDaPromocao = { nome: "", chamada: "", tema: "MARCA", tipo: "PERCENTUAL", percentual: "10", valor: 0, vigencia: "PERIODO", comeca: "", termina: "", dias: [], das: "", ate: "", deDia: "", ateDia: "", todosOsPlanos: true, planos: [], soClientes: false, cpfs: "" };

export function formularioDe(p: Promocao): FormularioDaPromocao {
  return {
    nome: p.nome,
    chamada: p.chamada ?? "",
    tema: p.tema,
    tipo: p.tipo === "VALOR" ? "VALOR" : "PERCENTUAL",
    percentual: String(p.percentual ?? 10),
    valor: p.valor_centavos ?? 0,
    vigencia: (["PERIODO", "SEMANAL", "ANUAL"] as const).find((v) => v === p.vigencia) ?? "PERIODO",
    comeca: isoParaLocal(p.comeca_em),
    termina: isoParaLocal(p.termina_em),
    dias: [...p.dias_da_semana],
    das: p.hora_inicio ?? "",
    ate: p.hora_fim ?? "",
    deDia: mmddParaDdmm(p.anual_inicio),
    ateDia: mmddParaDdmm(p.anual_fim),
    todosOsPlanos: p.todos_os_planos,
    planos: [...p.planos],
    soClientes: p.so_clientes,
    cpfs: p.clientes.join("\n"),
  };
}

/**
 * O corpo do POST e do PUT. So os campos da vigencia escolhida vao preenchidos; o que estiver errado o servico recusa
 * com a frase pra mostrar (a tela nao repete a conferencia dele).
 */
export function corpoDaPromocao(f: FormularioDaPromocao) {
  return {
    nome: f.nome.trim(),
    chamada: f.chamada.trim() || null,
    tema: f.tema,
    tipo: f.tipo,
    percentual: f.tipo === "PERCENTUAL" ? Number(f.percentual) || null : null,
    valor_centavos: f.tipo === "VALOR" ? f.valor || null : null,
    vigencia: f.vigencia,
    comeca_em: f.vigencia === "PERIODO" ? localParaIso(f.comeca) : null,
    termina_em: f.vigencia === "PERIODO" ? localParaIso(f.termina) : null,
    dias_da_semana: f.vigencia === "SEMANAL" ? [...f.dias].sort((a, b) => a - b) : [],
    hora_inicio: f.vigencia === "SEMANAL" ? f.das || null : null,
    hora_fim: f.vigencia === "SEMANAL" ? f.ate || null : null,
    anual_inicio: f.vigencia === "ANUAL" ? ddmmParaMmdd(f.deDia) : null,
    anual_fim: f.vigencia === "ANUAL" ? ddmmParaMmdd(f.ateDia) : null,
    todos_os_planos: f.todosOsPlanos,
    planos: f.todosOsPlanos ? [] : f.planos,
    so_clientes: f.soClientes,
    clientes: f.soClientes ? cpfsDoTexto(f.cpfs) : [],
  };
}
